import { useEffect, useMemo, useState } from "react";
import DeploymentForm from "./DeploymentForm";
import { useLocation, useParams } from "react-router-dom";
import { LocationResponseDto, ProjectResponseDto } from "../common/ObjectTypes";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  createDeployment,
  getDeploymentByGuid,
  updateDeployment,
} from "../../api/deploymentsApi";
import { useProfile } from "../common/ProfileProvider";
import {
  getDefaultSensorsSearchQuery,
  SensorSearchQueryType,
} from "../common/QueryTypes";
import { getSensors } from "../../api/sensorsApi";
import { getLocations } from "../../api/locationsApi";
import { getProjectByGuid } from "../../api/projectsApi";
import { formatDateToYYYYMMDD } from "../common/helpers";
import { useNavigate } from "react-router-dom";
import Ajv from "ajv";
import ImportFromCsv from "../csvImport/ImportFromCsv";
import FollowImportUntilSettled from "../csvImport/FollowImportUntilSettled";
import { appliedTemplate, schemaColumns } from "../csvImport/csvImport";
import { getCsvImport } from "../../api/migrationsApi";
import type { CsvImportJobDto } from "../../api/migrationsApi";
import { canOpenWildlifeInsightsMigration } from "../projects/migrationEntry";
import { useUnsavedChanges } from "../common/UnsavedChanges";
import { deploymentHasUnsavedChanges } from "./deploymentUnsavedChanges";

type ManageDeploymentDetailsProps = {
  projectId: number;
  projectGuid: string;
  /**
   * The organisation the project belongs to. `ManageProjectDetails` derives it with
   * `Number(...)` from state its project load fills and still mounts this page when that
   * load fails, so it can be NaN (or absent); the CSV import (AKL-815) is withheld unless
   * both it and `projectId` are real numbers, because `ImportFromCsv` sends them as
   * `org_id` and `target_project_id` and the API refuses an upload naming neither.
   */
  orgId?: number;
  perms: any[];
  existingDeployment: boolean;
  /**
   * Called once when an applied import's report says it applied a deployment template to
   * the project (the Wildlife Insights format does that to a project without one), so the
   * owner of the project state can re-read it: `ManageProjectDetails` keeps the template it
   * loaded with the page, and the Deployments tab this page leaves for reads it from there.
   * Called whether the dialog saw the job finish (`handleImported`) or a follower on this
   * page did (`handleImportSettled`).
   */
  onTemplateApplied?: () => void;
};
const ManageDeploymentDetails = ({
  projectId,
  projectGuid,
  orgId,
  perms,
  existingDeployment,
  onTemplateApplied,
}: ManageDeploymentDetailsProps) => {
  const { deploymentGuid } = useParams();
  const navigate = useNavigate();
  const ajv = new Ajv({
    allErrors: true,
    strict: false,
    keywords: ["example"],
  });
  const { t } = useTranslation();
  const { profile, profileLoading } = useProfile();
  const [deployment, setDeployment] = useState<any>({
    deployment_id: null,
    start_date: "",
    end_date: "",
    project_id: projectId,
  });
  const [originalDeployment, setOriginalDeployment] = useState<any>(deployment);
  const [project, setProject] = useState<ProjectResponseDto | undefined>(
    undefined,
  );
  const [errors, setErrors] = useState({});
  const [rjsfErrors, setRjsfErrors] = useState<
    Array<{ field: string; message: string }>
  >([]);
  const [canModifyDeployment, setCanModifyDeployment] = useState(false);
  const [canAddDeployment, setCanAddDeployment] = useState(false);
  const canUploadImages = perms.some((perm: any) =>
    perm.name.includes("UPLOAD_IMAGES"),
  );
  const [saving, setSaving] = useState(false);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [locations, setLocations] = useState<LocationResponseDto[]>([]);
  const location = useLocation();

  // AKL-1083. `originalDeployment` is the deployment as last loaded or saved; the edits
  // belong to this deployment's own address, so choosing another deployment asks too.
  const { confirmLeave, leaveWithoutAsking } = useUnsavedChanges(
    deploymentHasUnsavedChanges(
      deployment,
      originalDeployment,
      project?.deployment_schema,
    ),
    location.pathname,
  );
  const [sensors, setSensors] = useState<any[]>([]);
  const [savingAndAddAnother, setSavingAndAddAnother] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Imports the dialog let go of while the server was still applying them (AKL-815). This
  // page shows no list to refresh, but it must not forget them either: when the user leaves
  // for the deployments list they are handed over through router state (`goToList`), and
  // that tab follows them to the end and reports the outcome. Until then each keeps its own
  // poller here so a job that settles while the user is still on this page is dropped
  // cleanly rather than handed over already finished — and, when it put a deployment
  // template on the project, the template is refreshed here (`handleImportSettled`).
  const [pendingImportGuids, setPendingImportGuids] = useState<string[]>([]);

  // Only on the Add page, only with the create permission, and only with real ids for both
  // the organisation and the project (see the `orgId` prop). An existing deployment has
  // nothing to import into.
  const canImport =
    !existingDeployment &&
    canAddDeployment &&
    Number.isFinite(orgId) &&
    Number.isFinite(projectId);

  // "Migrate full export" hands off to this project's Edit Project page in target mode, so
  // the gate is the deep link's own (AKL-812): all three `MIGRATION_TARGET_PERMISSIONS`,
  // `is_admin` not counting — see `ManageProjectDeployments` for why the organisation gate
  // in csvImport.ts would be wrong here.
  const offerFullMigration = canOpenWildlifeInsightsMigration({
    existingProject: true,
    isAdmin: !!profile?.is_admin,
    organizationOptionCount: 0,
    perms,
  });

  // The columns this project's deployment template adds to the Akili layout. `undefined`
  // while the project is still loading, or when its load failed, so the modal never says
  // no template columns can be read from a project that may well have some; `[]` only once
  // the project is in hand and its template really adds none a cell can hold (or there is
  // no template).
  const extraColumns = useMemo(
    () =>
      project
        ? schemaColumns(project.deployment_schema, project.ui_schema)
        : undefined,
    [project],
  );

  const fetchLocations = async () => {
    const temp = await getLocations({
      project_id: projectId,
      all_locations: true,
    });
    setLocations(temp.locations);
  };

  const handleSelectDeployment = async (dep_guid: string) => {
    setDeploymentLoading(true);
    try {
      const dep = await getDeploymentByGuid(dep_guid);
      setDeployment(dep);
      setOriginalDeployment(dep);
    } catch (error) {
      console.error("Error loading deployment:", error);
    } finally {
      setDeploymentLoading(false);
    }
  };

  /*
   * Who may edit and who may create. Its own effect, keyed on `perms` as well: the project
   * page mounts this page with `perms=[]` and fills them from a separate request, so on a
   * direct load the permissions land after the first render. Kept apart from the data
   * loads below so their requests are not repeated when the permissions arrive.
   */
  useEffect(() => {
    if (!profileLoading && profile) {
      const isAdmin = profile?.is_admin;
      const hasPermissionToModifyDeps = perms.some((perm: any) =>
        perm.name.includes("EDIT_DEPLOYMENTS_LOCATIONS"),
      );
      const hasPermissionToCreateDeps = perms.some((perm: any) =>
        perm.name.includes("CREATE_DEPLOYMENTS_LOCATIONS"),
      );
      setCanModifyDeployment(isAdmin || hasPermissionToModifyDeps);
      setCanAddDeployment(isAdmin || hasPermissionToCreateDeps);
    }
  }, [profile, profileLoading, perms]);

  useEffect(() => {
    const fetchSensors = async () => {
      let query: SensorSearchQueryType = getDefaultSensorsSearchQuery(
        orgId as number,
      );
      query.project_id = projectId;
      query.all_sensors = true;
      const temp = await getSensors(query);
      setSensors(temp.sensors);
    };
    const loadProject = async () => {
      try {
        if (projectGuid) {
          const tempProject = await getProjectByGuid(projectGuid);
          if (!tempProject) {
            throw new Error("Project not found");
          }

          setProject(tempProject);
        }
      } catch (error) {
        console.error("Error loading project:", error);
      }
    };

    if (!profileLoading && profile) {
      fetchLocations();
      fetchSensors();
      loadProject();
    }
  }, [profile, profileLoading]);

  useEffect(() => {
    // if state contains deploymentId, open the modal
    if (deploymentGuid && deploymentGuid !== "add") {
      handleSelectDeployment(deploymentGuid);
      location.state = {};
    }
  }, [deploymentGuid]);

  const handleDeploymentFormChange = (e: any) => {
    setDeployment((prevDeployment: any) => ({
      ...prevDeployment,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectLocationInForm = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setDeployment((prevState: any) => ({
        ...prevState,
        location_id: selectedOption.value,
      }));
    }
    if (!selectedOption) {
      setDeployment((prevState: any) => ({
        ...prevState,
        location_id: null,
      }));
    }
  };
  const handleSelectSensorInForm = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setDeployment((prevState: any) => ({
        ...prevState,
        sensor_id: selectedOption.value,
      }));
    }
    if (!selectedOption) {
      setDeployment((prevState: any) => ({
        ...prevState,
        sensor_id: null,
      }));
    }
  };

  /**
   * Checks the form before Save. The template fields are checked against the project's
   * deployment template as written: a key the template does not list is not an error
   * (Akili-Wiki#653), so the `wildlife_insights` extras a Wildlife Insights migration keeps
   * are saved as they are. When no field shows an error (the template refuses the details
   * as a whole, or cannot be read), the failure is toasted, or Save would stop with nothing
   * on screen. When any field shows one, nothing is toasted.
   * @returns true when the deployment can be saved
   */
  const formIsValid = () => {
    let form_errors: any = {};
    let rsjf_errors: any[] = [];
    const templateRejected = t(
      "The project's deployment template does not accept this deployment's details, so it could not be saved.",
    );

    if (!deployment.deployment_name)
      form_errors.deployment_name = t("Name is required");

    try {
      const schema = project?.deployment_schema;

      if (schema) {
        const validate = ajv.compile(schema);

        const dataToValidate = deployment.schema_data || {};
        const valid = validate(dataToValidate);

        if (!valid) {
          if (!rsjf_errors) rsjf_errors = [];

          if (validate.errors) {
            validate.errors.forEach((error) => {
              const fieldPath = error.instancePath.startsWith("/")
                ? error.instancePath.substring(1)
                : error.instancePath;

              let field = fieldPath;
              let message = error.message || "Invalid value";

              // Helper function to format field names consistently
              const formatFieldName = (fieldName: string) => {
                return fieldName
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");
              };

              if (error.keyword === "required") {
                field = error.params.missingProperty;
                const displayField = formatFieldName(field);
                message = `${displayField} is required`;
              } else if (fieldPath === "") {
                field = "root";
              } else {
                const displayField = formatFieldName(field);
                message = `${displayField}: ${message}`;
              }

              if (!rsjf_errors.find((err: any) => err.field === field)) {
                rsjf_errors.push({
                  field: field,
                  message: message,
                });
              }
            });
          }
          // Toast only when no field shows an error: a field error can come with an error
          // about the whole (a failed `oneOf`), and then the field already says what is wrong.
          if (rsjf_errors.every((err: any) => err.field === "root"))
            form_errors.schema_data = templateRejected;
        }
      } else {
        console.log("No schema available for validation");
      }
    } catch (error) {
      console.error("Validation error:", error);
      form_errors.schema_data = templateRejected;
    }

    setErrors(form_errors);
    setRjsfErrors(rsjf_errors);
    if (form_errors.schema_data) toast.error(form_errors.schema_data);

    return Object.keys(form_errors).length === 0 && rsjf_errors.length === 0;
  };

  /**
   * Leave for the deployments list, handing over every import still being followed so the
   * list can refresh when they settle and tell the user how they ended (AKL-815). Plain
   * navigation when there is nothing to hand over, so the list's history entry carries no
   * state it would then have to clear.
   * @param extra guids to hand over beyond `pendingImportGuids` — the job the dialog was
   *   showing when it closed mid-apply
   */
  const goToList = (extra: string[]) => {
    const guids = Array.from(new Set([...pendingImportGuids, ...extra]));
    const path = `/projects/${projectGuid}/deployments`;
    if (guids.length > 0) {
      navigate(path, { state: { followImportGuids: guids } });
    } else {
      navigate(path);
    }
  };

  /*
   * The form's Cancel/Close. Pending followers go with the user (see `goToList`). Asks first
   * when the form holds unsaved changes (AKL-1083).
   */
  const onClose = () => {
    confirmLeave(() => goToList([]));
  };

  /*
   * The dialog is letting go of a job — on close, or when the user picks another file after
   * polling gave up. A job the server was still applying is followed from here until it
   * settles (see `pendingImportGuids`); a dry run wrote nothing, and an applied job is done.
   */
  const followIfStillApplying = (job: CsvImportJobDto | null) => {
    if (job?.status === "applying") {
      setPendingImportGuids((current) =>
        current.includes(job.guid) ? current : [...current, job.guid],
      );
    }
  };

  /*
   * A followed import has settled (or could not be checked on any more): stop following it
   * and read the job back. `FollowImportUntilSettled` does not pass the job through, so it
   * is read once more here, as the Deployments tab does. Nothing on this page lists
   * deployments, so there is no list to refresh, and the outcome is reported on the
   * deployments list only for jobs handed over still in flight — but a job that put a
   * deployment template on the project (a Wildlife Insights import the dialog let go of
   * mid-apply, PR #770 review) leaves the template stale in two places, exactly as one the
   * dialog saw finish does (`handleImported`): the project page's copy, refreshed through
   * `onTemplateApplied`, and this page's own `project`, re-read here so `extraColumns`
   * recomputes and the dialog still open on this page lists the new template columns and
   * offers a download with the new columns. A read that fails is logged only: this page
   * toasts nothing about followers, and the list would say the same after the hand-over.
   */
  const handleImportSettled = async (guid: string) => {
    setPendingImportGuids((current) => current.filter((g) => g !== guid));
    try {
      const job = await getCsvImport(guid);
      if (appliedTemplate(job)) {
        onTemplateApplied?.();
        const fresh = await getProjectByGuid(projectGuid);
        if (fresh) setProject(fresh);
      }
    } catch (err) {
      console.error("Error reading a settled CSV import or its project:", err);
    }
  };

  /*
   * An import was applied. Nothing on this page lists deployments, so there is no list to
   * refresh (Done on the applied job takes the user to the list, which loads fresh) — but a
   * report saying the import applied a deployment template to the project is passed up, so
   * the project page re-reads the template the list's next import dialog will describe.
   */
  const handleImported = (job: CsvImportJobDto) => {
    if (appliedTemplate(job)) onTemplateApplied?.();
  };

  /*
   * The import dialog closed. `applied`: the deployments exist, so go and show them.
   * `applying`: the same, handing the job over so the list refreshes and reports when it
   * ends — a closed dialog cannot be reopened to it. Anything else (no job, a dry run, a
   * failure the user has read) stays on the Add page; any follower already mounted keeps
   * polling on its own.
   */
  const handleImportClose = (lastJob: CsvImportJobDto | null) => {
    setImportOpen(false);
    if (lastJob?.status === "applied") {
      goToList([]);
    } else if (lastJob?.status === "applying") {
      goToList([lastJob.guid]);
    }
  };

  /*
   * The user chose a full Wildlife Insights migration instead: hand off to this project's
   * Edit Project page, which opens the migration modal in target mode from the
   * `?migrate=wi` deep link (AKL-812).
   */
  const handleMigrateFullExport = () => {
    setImportOpen(false);
    navigate(`/projects/${projectGuid}/overview?migrate=wi`);
  };

  const handleSave = async (addAnother: boolean) => {
    if (!formIsValid()) return;

    const deploymentData = {
      deployment_name: deployment.deployment_name,
      deployment_id: deployment.deployment_id,
      start_date: formatDateToYYYYMMDD(deployment.start_date),
      end_date: formatDateToYYYYMMDD(deployment.end_date),
      project_id: projectId,
      location_id: deployment.location_id,
      sensor_id: deployment.sensor_id,
      schema_data: deployment.schema_data,
    };

    try {
      // Each save makes what the form shows the saved state, so nothing asks about it
      // afterwards (AKL-1083). Before this, an update left `originalDeployment` at the
      // loaded values, so Save stayed enabled after saving.
      if (existingDeployment && deployment.deployment_id) {
        setSaving(true);
        await updateDeployment(deploymentData);
        setOriginalDeployment(deployment);
        toast.success(t("Deployment updated"));
      } else {
        if (addAnother) {
          setSavingAndAddAnother(true);
          await createDeployment(deploymentData);
          // The form keeps these values as the start of the next deployment.
          setOriginalDeployment(deployment);
          toast.success(t("Deployment added"));
        } else {
          setSaving(true);
          await createDeployment(deploymentData);
          toast.success(t("Deployment added"));
          leaveWithoutAsking(() => goToList([]));
        }
      }
    } catch (error: any) {
      const action = existingDeployment ? "updating" : "adding";
      toast.error(
        t(`Error ${action} deployment`) + ": " + error.data.detail.toString(),
      );
    } finally {
      setSaving(false);
      setSavingAndAddAnother(false);
    }
  };

  return (
    <div>
      {/* Never mounted without CREATE_DEPLOYMENTS_LOCATIONS: the API's 403 on the upload
          would be a dead end for the user (no longer a sign-out since AKL-939, but still
          a bundle chosen for nothing). `orgId` is a real number inside
          this guard (`canImport` checks it), hence the cast. Nothing on this page lists
          deployments, so `onImported` refreshes nothing here (Done on an applied job takes
          the user to the list, which loads fresh); it only reports a template the import
          applied — see `handleImported`. */}
      {canImport && importOpen && (
        <div className="fixed z-50">
          <ImportFromCsv
            isOpen={importOpen}
            orgId={orgId as number}
            entity="deployments"
            formats={["akili_csv", "ctms_csv"]}
            targetProjectId={projectId}
            extraColumns={extraColumns}
            canMigrateFullExport={offerFullMigration}
            onClose={handleImportClose}
            onImported={handleImported}
            onMigrateFullExport={handleMigrateFullExport}
            onJobAbandoned={followIfStillApplying}
          />
        </div>
      )}
      {/* One follower per import the dialog let go of mid-apply, until the user leaves for
          the list (which takes them over) or the job settles — `handleImportSettled` then
          reads it back and refreshes a template it applied. Render nothing visible. */}
      {pendingImportGuids.map((guid) => (
        <FollowImportUntilSettled
          key={guid}
          guid={guid}
          onSettled={handleImportSettled}
        />
      ))}
      <DeploymentForm
        deployment={deployment}
        project={project}
        projectGuid={projectGuid}
        deploymentGuid={deploymentGuid || ""}
        deploymentLoading={deploymentLoading}
        locations={locations}
        sensors={sensors}
        onClose={onClose}
        onSave={handleSave}
        onSelectLocation={handleSelectLocationInForm}
        onSelectSensor={handleSelectSensorInForm}
        onChange={handleDeploymentFormChange}
        errors={errors}
        rjsfErrors={rjsfErrors}
        saving={saving}
        savingAndAddAnother={savingAndAddAnother}
        existingDeployment={existingDeployment}
        canModify={canModifyDeployment}
        saveDisabled={
          JSON.stringify(deployment) === JSON.stringify(originalDeployment)
        }
        canUploadImages={canUploadImages}
        onImportFromCsv={canImport ? () => setImportOpen(true) : undefined}
      />
    </div>
  );
};

export default ManageDeploymentDetails;
